/**
 * The studio on one canvas: every client as a card, laid out like the 2D
 * board the studio used before, and moved around the same way — drag to pan,
 * ctrl + scroll or two fingers to zoom.
 *
 * Each card is the client page cut to what a glance needs: who owns it, who
 * we talk to there, this month's deliverables against what has shipped, and
 * the work still open — tasks and pieces together, overdue first. Nothing on
 * it is typed in twice: the counts are the same commitments the client page
 * and The month read, and a card opens the client.
 */

import { Maximize2, Minus, Plus, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppData } from '../context/AppDataContext'
import { api } from '../lib/api'
import { cn } from '../lib/cn'
import { monthName, shortDate, today } from '../lib/format'
import type { StudioTask } from './tasks/types'

interface Progress {
  commitment_id: string
  metric: string
  unit: string
  target_count: number
  delivered: number
  in_flight: number
}

interface Piece {
  id: string
  title: string
  kind: string
  stage: string
  owner_id: string | null
  due: string | null
  scheduled_for: string | null
}

interface ClientView {
  month: string
  progress: Progress[]
  pieces: Piece[]
  team: { id: string; name: string; assignment: 'lead' | 'member' | 'view' }[]
  contacts: { id: string; name: string; role: string }[]
  health: { behind: boolean; overdue_count: number; committed: number; delivered: number }
}

const STAGE_LABEL: Record<string, string> = {
  briefed: 'Briefed',
  shot: 'Shot',
  editing: 'Editing',
  internal_review: 'Review',
  with_client: 'With client',
  scheduled: 'Scheduled',
  posted: 'Posted'
}

const CARD_W = 300
const GAP = 28
const MIN_ZOOM = 0.3
const MAX_ZOOM = 1.6
const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))

interface OpenItem {
  key: string
  title: string
  who: string | null
  date: string | null
  overdue: boolean
  label: string
  kind: 'task' | 'piece'
}

export default function StudioCanvas() {
  const { currentClients, directory } = useAppData()
  const navigate = useNavigate()
  const [views, setViews] = useState<Record<string, ClientView | null>>({})
  const [tasks, setTasks] = useState<StudioTask[]>([])
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  const clients = useMemo(
    () => [...currentClients].sort((a, b) => a.name.localeCompare(b.name)),
    [currentClients]
  )
  const clientKey = clients.map((c) => c.id).join(',')

  useEffect(() => {
    let live = true
    setLoading(true)
    const ids = clientKey === '' ? [] : clientKey.split(',')
    void Promise.all([
      Promise.all(
        ids.map((id) =>
          api
            .get<ClientView>(`/clients/${encodeURIComponent(id)}`)
            .then((v) => [id, v] as const)
            .catch(() => [id, null] as const)
        )
      ),
      api.get<StudioTask[]>('/tasks').catch(() => [] as StudioTask[])
    ]).then(([pairs, t]) => {
      if (!live) return
      setViews(Object.fromEntries(pairs))
      setTasks(Array.isArray(t) ? t : [])
      setLoading(false)
    })
    return () => {
      live = false
    }
  }, [clientKey, nonce])

  const personName = useMemo(() => new Map(directory.map((p) => [p.id, p.name])), [directory])
  const now = today()

  const openFor = useCallback(
    (clientId: string, view: ClientView | null): OpenItem[] => {
      const items: OpenItem[] = []
      for (const t of tasks) {
        if (t.client_id !== clientId || t.status === 'done') continue
        const overdue = t.due !== null && t.due < now
        items.push({
          key: `t${t.id}`,
          title: t.title,
          who: t.assignee_ids.length === 0 ? null : t.assignee_ids.map((id) => personName.get(id) ?? id).join(', '),
          date: t.due,
          overdue,
          label: overdue ? 'Overdue' : t.status === 'in_progress' ? 'In progress' : 'To do',
          kind: 'task'
        })
      }
      for (const p of view?.pieces ?? []) {
        if (p.stage === 'posted') continue
        const overdue = p.due !== null && p.due < now
        items.push({
          key: `p${p.id}`,
          title: p.title,
          who: p.owner_id === null ? null : personName.get(p.owner_id) ?? p.owner_id,
          date: p.due ?? p.scheduled_for,
          overdue,
          label: overdue ? 'Overdue' : STAGE_LABEL[p.stage] ?? p.stage,
          kind: 'piece'
        })
      }
      return items.sort(
        (a, b) =>
          Number(b.overdue) - Number(a.overdue) ||
          (a.date ?? '9999').localeCompare(b.date ?? '9999') ||
          a.title.localeCompare(b.title)
      )
    },
    [tasks, personName, now]
  )

  /* ---- pan and zoom ---- */

  const [zoom, setZoom] = useState(0.8)
  const [pan, setPan] = useState({ x: 24, y: 24 })
  const viewport = useRef<HTMLDivElement>(null)
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const gesture = useRef<{ dist: number; zoom: number; moved: number } | null>(null)
  const dragged = useRef(0)

  const zoomAround = useCallback((next: number, cx: number, cy: number) => {
    setZoom((z) => {
      const nz = clampZoom(next)
      setPan((p) => ({ x: cx - ((cx - p.x) / z) * nz, y: cy - ((cy - p.y) / z) * nz }))
      return nz
    })
  }, [])

  const onWheel = (e: ReactWheelEvent<HTMLDivElement>) => {
    const box = viewport.current?.getBoundingClientRect()
    if (box === undefined) return
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      zoomAround(zoom * Math.exp(-e.deltaY * 0.0015), e.clientX - box.left, e.clientY - box.top)
    } else {
      setPan((p) => ({ x: p.x - e.deltaX, y: p.y - e.deltaY }))
    }
  }

  // Ctrl + wheel is the browser's own zoom unless the listener is not passive.
  useEffect(() => {
    const el = viewport.current
    if (el === null) return
    const stop = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) e.preventDefault()
    }
    el.addEventListener('wheel', stop, { passive: false })
    return () => el.removeEventListener('wheel', stop)
  }, [])

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    dragged.current = 0
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      gesture.current = { dist: Math.hypot(a!.x - b!.x, a!.y - b!.y), zoom, moved: 0 }
    }
  }

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const last = pointers.current.get(e.pointerId)
    if (last === undefined) return
    const dx = e.clientX - last.x
    const dy = e.clientY - last.y
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size === 2 && gesture.current !== null) {
      const [a, b] = [...pointers.current.values()]
      const box = viewport.current?.getBoundingClientRect()
      if (box !== undefined) {
        const d = Math.hypot(a!.x - b!.x, a!.y - b!.y)
        zoomAround(gesture.current.zoom * (d / gesture.current.dist), (a!.x + b!.x) / 2 - box.left, (a!.y + b!.y) / 2 - box.top)
      }
      dragged.current += 10
      return
    }
    if (pointers.current.size === 1 && (e.buttons & 1) === 1) {
      dragged.current += Math.abs(dx) + Math.abs(dy)
      if (dragged.current > 4) {
        if (!(e.target as Element).hasPointerCapture?.(e.pointerId)) viewport.current?.setPointerCapture(e.pointerId)
        setPan((p) => ({ x: p.x + dx, y: p.y + dy }))
      }
    }
  }

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId)
    if (pointers.current.size < 2) gesture.current = null
  }

  const columns = Math.max(1, Math.min(6, clients.length))
  const stageWidth = columns * CARD_W + (columns - 1) * GAP

  const fit = () => {
    const box = viewport.current?.getBoundingClientRect()
    if (box === undefined) return
    const z = clampZoom((box.width - 48) / stageWidth)
    setZoom(z)
    setPan({ x: Math.max(24, (box.width - stageWidth * z) / 2), y: 24 })
  }

  const open = (id: string) => {
    if (dragged.current > 4) return
    navigate(`/clients/${encodeURIComponent(id)}`)
  }

  const monthLabel = monthName(`${now.slice(0, 7)}-01`)

  return (
    <div className="flex h-dvh flex-col gap-3 bg-shell p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-display text-xl font-semibold text-shell-text-active">Client canvas</h1>
          <p className="text-sm text-shell-text">
            Every client on one board, for {monthLabel}. Drag to move around; ctrl + scroll or pinch to zoom.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-xl border border-shell-border bg-shell p-1 shadow-card">
          <button type="button" aria-label="Zoom out" onClick={() => setZoom((z) => clampZoom(z - 0.1))} className="rounded-lg p-1.5 text-shell-text hover:bg-shell-border/50">
            <Minus size={15} />
          </button>
          <span className="w-12 text-center text-xs tabular-nums text-shell-text">{Math.round(zoom * 100)}%</span>
          <button type="button" aria-label="Zoom in" onClick={() => setZoom((z) => clampZoom(z + 0.1))} className="rounded-lg p-1.5 text-shell-text hover:bg-shell-border/50">
            <Plus size={15} />
          </button>
          <button type="button" aria-label="Fit to screen" onClick={fit} className="rounded-lg p-1.5 text-shell-text hover:bg-shell-border/50">
            <Maximize2 size={15} />
          </button>
          <button type="button" aria-label="Refresh" onClick={() => setNonce((n) => n + 1)} className="rounded-lg p-1.5 text-shell-text hover:bg-shell-border/50">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div
        ref={viewport}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="relative flex-1 cursor-grab touch-none overflow-hidden rounded-card border border-shell-border active:cursor-grabbing"
        style={{
          backgroundColor: 'var(--color-shell-border, #e5e7eb)',
          backgroundImage: 'radial-gradient(circle, rgba(120,120,140,0.35) 1px, transparent 1.2px)',
          backgroundSize: `${22 * zoom}px ${22 * zoom}px`,
          backgroundPosition: `${pan.x}px ${pan.y}px`
        }}
      >
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, width: stageWidth }}
        >
          <div className="flex flex-wrap items-start" style={{ gap: GAP }}>
            {clients.map((c) => {
              const view = views[c.id]
              const items = openFor(c.id, view ?? null)
              const leads = (view?.team ?? []).filter((t) => t.assignment === 'lead')
              const owners = leads.length > 0 ? leads : (view?.team ?? []).filter((t) => t.assignment === 'member').slice(0, 1)
              const overdue = items.filter((i) => i.overdue).length
              const badge =
                c.accountStatus === 'Prospect'
                  ? { text: 'Prospect', tone: 'bg-sky-100 text-sky-700' }
                  : overdue > 0
                    ? { text: `${overdue} overdue`, tone: 'bg-red-100 text-red-700' }
                    : view?.health.behind
                      ? { text: 'Behind', tone: 'bg-amber-100 text-amber-800' }
                      : { text: 'On track', tone: 'bg-emerald-100 text-emerald-700' }
              return (
                <article
                  key={c.id}
                  style={{ width: CARD_W }}
                  className="overflow-hidden rounded-2xl border border-shell-border bg-shell text-left shadow-card"
                >
                  <button
                    type="button"
                    onClick={() => open(c.id)}
                    className="block w-full px-4 pb-3 pt-3 text-left hover:bg-shell-border/20"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[11px] text-shell-text">{c.industry}</span>
                      <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold', badge.tone)}>{badge.text}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2.5">
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
                        style={{ backgroundColor: c.color }}
                      >
                        {c.initials}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-shell-text-active">{c.name}</p>
                        <p className="truncate text-[11px] text-shell-text">
                          {c.industry} • {monthLabel}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-shell-text/70">Owner</span>
                      {owners.length === 0 ? (
                        <span className="text-[11px] text-shell-text/60">Nobody yet</span>
                      ) : (
                        owners.map((o) => (
                          <span key={o.id} className="rounded-full bg-brand-500/10 px-2 py-0.5 text-[11px] font-medium text-brand-700">
                            {o.name}
                          </span>
                        ))
                      )}
                    </div>
                  </button>

                  <section className="border-t border-shell-border bg-shell-border/20 px-4 py-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-shell-text/70">Who we talk to</p>
                    <p className="mt-1 text-xs text-shell-text-active">
                      {view === undefined
                        ? '…'
                        : (view?.contacts ?? []).length === 0
                          ? <span className="text-shell-text/60">Nobody on file</span>
                          : view!.contacts.map((k) => (k.role ? `${k.name} (${k.role})` : k.name)).join(', ')}
                    </p>
                  </section>

                  <section className="border-t border-shell-border px-4 py-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-shell-text/70">Monthly deliverables</p>
                    <ul className="mt-1.5 flex flex-col gap-1.5">
                      {(view?.progress ?? []).length === 0 && (
                        <li className="text-xs text-shell-text/60">{view === undefined ? '…' : 'Nothing committed this month'}</li>
                      )}
                      {(view?.progress ?? []).map((p) => {
                        const met = p.target_count > 0 && p.delivered >= p.target_count
                        return (
                          <li
                            key={p.commitment_id}
                            className={cn(
                              'flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-xs',
                              met ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-shell-border text-shell-text-active'
                            )}
                          >
                            <span className="truncate">{p.metric}</span>
                            <span className="shrink-0 tabular-nums font-medium">
                              {p.delivered}/{p.target_count}
                              {p.in_flight > 0 && <span className="ml-1 text-[10px] font-normal text-shell-text">+{p.in_flight}</span>}
                            </span>
                          </li>
                        )
                      })}
                    </ul>
                  </section>

                  <section className="border-t border-shell-border px-4 pb-3 pt-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-shell-text/70">Active tasks &amp; reels</p>
                    <ul className="mt-1.5 flex flex-col gap-1.5">
                      {items.length === 0 && <li className="py-2 text-center text-xs text-shell-text/60">{view === undefined ? '…' : 'Nothing open'}</li>}
                      {items.slice(0, 8).map((i) => (
                        <li
                          key={i.key}
                          className={cn(
                            'rounded-lg border border-l-[3px] px-2.5 py-1.5',
                            i.overdue ? 'border-red-200 border-l-red-400 bg-red-50/60' : 'border-shell-border border-l-brand-400'
                          )}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className={cn('truncate rounded-full px-1.5 py-0.5 text-[10px]', i.who === null ? 'bg-shell-border/60 text-shell-text' : 'bg-brand-500/10 text-brand-700')}>
                              {i.who ?? 'Unassigned'}
                            </span>
                            <span className={cn('shrink-0 text-[10px] font-semibold', i.overdue ? 'text-red-600' : 'text-amber-700')}>{i.label}</span>
                          </div>
                          <p className="mt-1 line-clamp-2 text-xs text-shell-text-active">{i.title}</p>
                          <p className="mt-0.5 text-[10px] text-shell-text/70">
                            {i.kind === 'piece' ? 'Piece' : 'Task'}
                            {i.date !== null && ` · ${shortDate(i.date)}`}
                          </p>
                        </li>
                      ))}
                      {items.length > 8 && (
                        <li>
                          <button type="button" onClick={() => open(c.id)} className="text-xs font-medium text-brand-600 hover:underline">
                            {items.length - 8} more on the client page
                          </button>
                        </li>
                      )}
                    </ul>
                  </section>
                </article>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
